import React, { useState, useEffect } from 'react';
import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  CardContent,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Grid
} from '@mui/material';
import { Brain, ArrowLeft, ArrowRight, CheckCircle, Plus, Settings, Layers } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import InferenceStepBox from '../Boxes/Steps/InferenceStepBox';
import CodeStepBox from '../Boxes/Steps/CodeStepBox';
import ApiStepBox from '../Boxes/Steps/ApiStepBox';
import InferenceChainStep from '../../models/Steps/InferenceChainStep';
import CodeStep from '../../models/Steps/CodeStep';
import ApiStep from '../../models/Steps/ApiStep';
import ProceduralMemory from '../../models/Memories/ProceduralMemory';
import { AVAILABLE_MODELS, DEFAULT_MODEL, DEFAULT_TEMPERATURE } from '../../config/models';
import CognitiveAbilityStepsModal from './CognitiveAbilityStepsModal';
import CognitiveAbilitiesModal from './CognitiveAbilitiesModal';

const getCostColor = (cost: string) => {
  switch (cost) {
    case 'Very High': return 'red';
    case 'High': return 'orange';
    case 'Medium': return 'blue';
    case 'Low': return 'green';
    case 'Very Low': return 'cyan';
    default: return 'default';
  }
};

interface ProceduralMemoryWizardModalProps {
  open: boolean;
  onClose: () => void;
  proceduralMemory: ProceduralMemory;
  onUpdate: (memory: ProceduralMemory) => void;
  workflowInputs: Record<string, any>;
  setWorkflowInputs: (inputs: Record<string, any>) => void;
  nodeType?: string;
}

const ProceduralMemoryWizardModal: React.FC<ProceduralMemoryWizardModalProps> = ({
  open,
  onClose,
  proceduralMemory,
  onUpdate,
  workflowInputs,
  setWorkflowInputs,
  nodeType = 'agent'
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [showStepsModal, setShowStepsModal] = useState(false);
  const [showCognitiveModal, setShowCognitiveModal] = useState(false);
  const [selectedCognitiveAbility, setSelectedCognitiveAbility] = useState<'learn' | 'retrieve' | 'reason' | 'design' | 'reflect'>('learn');
  const [cognitiveAbilities, setCognitiveAbilities] = useState({
    learn: { enabled: false, steps: [] },
    retrieve: { enabled: false, steps: [] },
    reason: { enabled: false, steps: [] },
    design: { enabled: false, steps: [] },
    reflect: { enabled: false, steps: [] }
  });
  const [hasCognitiveAbilities, setHasCognitiveAbilities] = useState(false);

  const handleCognitiveAbilityUpdate = (abilityType: string, abilityData: any) => {
    setCognitiveAbilities(prev => ({
      ...prev,
      [abilityType]: abilityData
    }));
  };

  const handleCognitiveAbilitiesUpdate = (abilities: any) => {
    setCognitiveAbilities(abilities);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Brain size={24} />
            <Typography variant="h6">Procedural Memory Wizard</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box p={2}>
            <Typography variant="body1">
              Configure your procedural memory settings here.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={onClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {hasCognitiveAbilities && (
        <>
          <CognitiveAbilityStepsModal
            open={showStepsModal}
            onClose={() => setShowStepsModal(false)}
            abilityType={selectedCognitiveAbility}
            abilityData={cognitiveAbilities[selectedCognitiveAbility]}
            onUpdate={handleCognitiveAbilityUpdate}
            workflowInputs={workflowInputs}
            setWorkflowInputs={setWorkflowInputs}
          />
          
          <CognitiveAbilitiesModal
            open={showCognitiveModal}
            onClose={() => setShowCognitiveModal(false)}
            cognitiveAbilities={cognitiveAbilities}
            onUpdate={handleCognitiveAbilitiesUpdate}
            workflowInputs={workflowInputs}
            setWorkflowInputs={setWorkflowInputs}
          />
        </>
      )}
    </>
  );
};

export default ProceduralMemoryWizardModal;